import { SelectMenu } from '@scale/scaleui';
import {
  DEFAULT_PROMPT_INPUT_SUBTITLE,
  DEFAULT_PROMPT_INPUT_TITLE,
  InteractionStepType,
  ChatMessageDisplayType,
} from '@scale/shared/types/chat';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import OverrideIDBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/OverrideIDBuilder';
import {
  ConditionalStepConfig,
  ReviewLevelConfig,
  EnableRTLTextConfig,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import { AdditionalConstraint } from '@scale/shared-types';
import ResponseConstraintsBuilder from 'react-scale/corp/components/QM/pipeline/ResponseConstraintsBuilder';
import { typeBuilderMap } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemas';
import produce from 'immer';
import { PROMPT_CONSTRAINTS_HINT } from 'react-scale/lib/ml_constants';
import { memo, useEffect } from 'react';
import { StepConfigurationComponentProps } from '../stepDescriptor/types';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';

function PromptInputConfigurationComponent({
  params: currentParams,
  setParams: setCurrentParams,
  previousParams,
  taskParams,
  displayMLQualitySettings,
}: StepConfigurationComponentProps<InteractionStepType.PromptInput>) {
  useEffect(() => {
    if (
      !currentParams.params.generatePrompts &&
      currentParams.params.systemPromptForPromptGeneration
    ) {
      setCurrentParams({
        ...currentParams,
        params: {
          ...currentParams.params,
          systemPromptForPromptGeneration: undefined,
        },
      });
    }
  }, [currentParams, currentParams.params.generatePrompts, setCurrentParams]);
  return (
    <div className="flex flex-col gap-4">
      <StringInputBuilder
        name={'Title'}
        placeholder={DEFAULT_PROMPT_INPUT_TITLE}
        existingValue={currentParams.title}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, title: value })}
        required={true}
      />

      <OverrideIDBuilder
        params={currentParams}
        previousParams={previousParams}
        setParams={setCurrentParams}
      />

      <StringInputBuilder
        name={'Subtitle'}
        placeholder={DEFAULT_PROMPT_INPUT_SUBTITLE}
        existingValue={currentParams.subtitle}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, subtitle: value })}
      />

      {displayMLQualitySettings && (
        <ResponseConstraintsBuilder
          typeBuilderMap={typeBuilderMap}
          name={'Prompt Constraints'}
          existingValue={currentParams.params.prompt_constraints ?? []}
          updateProperty={(value: AdditionalConstraint[] | undefined) =>
            setCurrentParams(
              produce(currentParams, draft => {
                draft.params.prompt_constraints = value;
                return draft;
              }),
            )
          }
          required={false}
          hint={PROMPT_CONSTRAINTS_HINT}
          taskTemplateParams={taskParams}
        />
      )}

      <SelectMenu
        label={'Display Type'}
        options={[
          { name: 'Markdown', id: ChatMessageDisplayType.Markdown },
          { name: 'Plaintext', id: ChatMessageDisplayType.Plaintext },
        ]}
        selectedOptionId={currentParams.params.display_type ?? ChatMessageDisplayType.Markdown}
        setSelected={(id: ChatMessageDisplayType) =>
          setCurrentParams(
            produce(currentParams, draft => {
              draft.params.display_type = id;
              return draft;
            }),
          )
        }
      />

      <StringInputBuilder
        name={'Instructions'}
        hint={'Instructions for the user to follow. This will be displayed above the prompt input.'}
        existingValue={currentParams.params.instructions}
        updateProperty={(value: string) =>
          setCurrentParams(
            produce(currentParams, draft => {
              draft.params.instructions = value;
              return draft;
            }),
          )
        }
        useMarkdownEditor={
          (currentParams.params.display_type ?? ChatMessageDisplayType.Markdown) ===
          ChatMessageDisplayType.Markdown
        }
      />

      <StringInputBuilder
        name={'Initial Prompt Text'}
        hint={
          'The initial prompt text. This can work with template variables and JSONata expressions for extracting fields in other fields.'
        }
        existingValue={currentParams.params.initial_prompt_text}
        updateProperty={(value: string) =>
          setCurrentParams(
            produce(currentParams, draft => {
              draft.params.initial_prompt_text = value;
              return draft;
            }),
          )
        }
        useMarkdownEditor={
          (currentParams.params.display_type ?? ChatMessageDisplayType.Markdown) ===
          ChatMessageDisplayType.Markdown
        }
      />

      <EnableRTLTextConfig
        value={currentParams.params.enable_rtl_text}
        onChange={value => {
          setCurrentParams(
            produce(currentParams, draft => {
              draft.params.enable_rtl_text = value;
              return draft;
            }),
          );
        }}
      />

      <BooleanInputBuilder
        name={'Enable Audio Recording'}
        existingValue={currentParams.params.enableAudioRecording ?? false}
        updateProperty={(value: any) => {
          setCurrentParams(
            produce(currentParams, draft => {
              draft.params.enableAudioRecording = value;
              return draft;
            }),
          );
        }}
        required={false}
        hint="If enabled, users can record audio as a prompt input"
      />

      <ReviewLevelConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />
      <ConditionalStepConfig
        currentParams={currentParams}
        setCurrentParams={setCurrentParams}
        previousParams={previousParams}
        taskParams={taskParams}
      />
    </div>
  );
}
export default memo(PromptInputConfigurationComponent);
